import { useCallback, useEffect, useState } from 'react'
import { Box, Flex, Text, VStack, Spinner, Table as ChakraTable } from '@chakra-ui/react'
import { DatePicker } from '@minetrace/ui/forms'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import {
  LuArrowLeft,
  LuShieldCheck,
  LuShieldAlert,
  LuRefreshCw,
  LuZap,
  LuCalendarRange,
  LuTriangleAlert,
} from 'react-icons/lu'
import { themeColors, brand, statusColors } from '../../theme'
import { auditService } from '../../api/services/audit.service'
import type { TableAuditVerificationResponse, AuditTableSummary } from '../../api/models/response'

const MotionBox = motion.create(Box)

const entityLabels: Record<string, string> = {
  Lote: 'Lotes de Mineral',
  ShipmentRecord: 'Registros de Traslado',
  ShipmentLocationPoint: 'Puntos de Ubicacion',
  ShipmentPhoto: 'Fotos de Traslado',
  Recepcion: 'Recepciones',
  MiningSupplier: 'Proveedores Mineros',
  MiningRight: 'Derechos Mineros',
  MiningRightComponent: 'Componentes de DM',
}

export default function DetalleTablaPage() {
  const { entityName } = useParams<{ entityName: string }>()
  const navigate = useNavigate()
  const colors = themeColors

  const [summary, setSummary] = useState<AuditTableSummary | null>(null)
  const [result, setResult] = useState<TableAuditVerificationResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingSummary, setLoadingSummary] = useState(true)

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  // Range picker state
  const [showRangePicker, setShowRangePicker] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const loadSummary = useCallback(async () => {
    if (!entityName) return
    setLoadingSummary(true)
    try {
      const summaries = await auditService.getSummary()
      const found = summaries.find((s) => s.entityName === entityName)
      if (found) setSummary(found)
    } finally {
      setLoadingSummary(false)
    }
  }, [entityName])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  const runVerification = async (type: string, from?: string, to?: string) => {
    if (!entityName) return
    setLoading(true)
    setResult(null)
    try {
      const data = await auditService.verifyTable(entityName, {
        verificationType: type,
        dateFrom: from || undefined,
        dateTo: to || undefined,
      })
      setResult(data)
      // Reload summary to get updated last verification
      await loadSummary()
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyNew = () => runVerification('NEW')

  const handleVerifyFull = () => {
    setShowConfirmModal(true)
  }

  const confirmFullVerify = () => {
    setShowConfirmModal(false)
    runVerification('FULL')
  }

  const handleVerifyRange = () => {
    if (dateFrom && dateTo) {
      setShowRangePicker(false)
      runVerification('RANGE', dateFrom, dateTo)
    }
  }

  const lastV = summary?.lastVerification

  return (
    <VStack gap={6} alignItems="stretch">
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        {/* Header */}
        <Flex alignItems="center" gap={3} mb={6}>
          <Box
            as="button"
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="36px"
            height="36px"
            borderRadius="lg"
            cursor="pointer"
            bg={colors.bgCard}
            border="1px solid"
            borderColor={colors.border}
            _hover={{ bg: colors.bgHover }}
            onClick={() => navigate('/app/auditoria')}
          >
            <LuArrowLeft size={18} color={colors.text} />
          </Box>
          <Box flex={1}>
            <Text fontSize="xl" fontWeight="700" color={colors.text}>
              {entityLabels[entityName || ''] || entityName}
            </Text>
            <Text fontSize="sm" color={colors.textMuted}>
              {loadingSummary ? 'Cargando...' : `${summary?.totalRecords ?? 0} registros auditados`}
            </Text>
          </Box>
        </Flex>

        {/* Last verification info */}
        {lastV && (
          <Box bg={colors.bgCard} border="1px solid" borderColor={colors.border} borderRadius="xl" p={4} mb={4}>
            <Text fontSize="xs" fontWeight="600" color={colors.textMuted} mb={2}>
              Última Verificación
            </Text>
            <Flex gap={6} fontSize="sm" flexWrap="wrap">
              <Box>
                <Text fontSize="xs" color={colors.textDimmed}>
                  Fecha
                </Text>
                <Text fontWeight="600" color={colors.text}>
                  {new Date(lastV.verifiedAt).toLocaleString('es-PE')}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color={colors.textDimmed}>
                  Usuario
                </Text>
                <Text fontWeight="600" color={colors.text}>
                  {lastV.verifiedBy}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color={colors.textDimmed}>
                  Tipo
                </Text>
                <Text fontWeight="600" color={colors.text}>
                  {lastV.verificationType}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color={colors.textDimmed}>
                  Resultado
                </Text>
                <Flex gap={2}>
                  <Text fontWeight="600" color={statusColors.success}>
                    {lastV.validCount} validos
                  </Text>
                  {lastV.tamperedCount > 0 && (
                    <Text fontWeight="600" color={statusColors.error}>
                      {lastV.tamperedCount} alterados
                    </Text>
                  )}
                </Flex>
              </Box>
              <Box>
                <Text fontSize="xs" color={colors.textDimmed}>
                  Duracion
                </Text>
                <Text fontWeight="600" color={colors.text}>
                  {lastV.durationMs}ms
                </Text>
              </Box>
            </Flex>
          </Box>
        )}

        {/* Action buttons */}
        <Flex gap={3} mb={6} flexWrap="wrap">
          <Box
            as="button"
            px={4}
            py={2.5}
            borderRadius="lg"
            fontSize="sm"
            fontWeight="600"
            bg={brand.green}
            color="white"
            cursor="pointer"
            _hover={{ bg: brand.greenDark }}
            onClick={handleVerifyNew}
            opacity={loading ? 0.6 : 1}
          >
            <Flex alignItems="center" gap={2}>
              <LuZap size={16} />
              <Text>Verificar Nuevos</Text>
            </Flex>
          </Box>

          <Box
            as="button"
            px={4}
            py={2.5}
            borderRadius="lg"
            fontSize="sm"
            fontWeight="600"
            bg={colors.bgCard}
            color={colors.text}
            border="1px solid"
            borderColor={colors.border}
            cursor="pointer"
            _hover={{ bg: colors.bgHover }}
            onClick={handleVerifyFull}
            opacity={loading ? 0.6 : 1}
          >
            <Flex alignItems="center" gap={2}>
              <LuRefreshCw size={16} />
              <Text>Verificar Todo</Text>
            </Flex>
          </Box>

          <Box
            as="button"
            px={4}
            py={2.5}
            borderRadius="lg"
            fontSize="sm"
            fontWeight="600"
            bg={colors.bgCard}
            color={colors.text}
            border="1px solid"
            borderColor={colors.border}
            cursor="pointer"
            _hover={{ bg: colors.bgHover }}
            onClick={() => setShowRangePicker(!showRangePicker)}
            opacity={loading ? 0.6 : 1}
          >
            <Flex alignItems="center" gap={2}>
              <LuCalendarRange size={16} />
              <Text>Verificar por Rango</Text>
            </Flex>
          </Box>
        </Flex>

        {/* Range picker */}
        {showRangePicker && (
          <Box bg={colors.bgCard} border="1px solid" borderColor={colors.border} borderRadius="xl" p={4} mb={4}>
            <Text fontSize="sm" fontWeight="600" color={colors.text} mb={3}>
              Seleccionar rango de fechas
            </Text>
            <Flex gap={3} alignItems="end" flexWrap="wrap">
              <Box>
                <Text fontSize="xs" color={colors.textMuted} mb={1}>
                  Desde
                </Text>
                <DatePicker value={dateFrom} onChange={setDateFrom} />
              </Box>
              <Box>
                <Text fontSize="xs" color={colors.textMuted} mb={1}>
                  Hasta
                </Text>
                <DatePicker value={dateTo} onChange={setDateTo} />
              </Box>
              <Box
                as="button"
                px={4}
                py={2}
                borderRadius="lg"
                fontSize="sm"
                fontWeight="600"
                bg={brand.green}
                color="white"
                cursor="pointer"
                _hover={{ bg: brand.greenDark }}
                onClick={handleVerifyRange}
                opacity={!dateFrom || !dateTo ? 0.5 : 1}
              >
                Verificar
              </Box>
              <Box
                as="button"
                px={4}
                py={2}
                borderRadius="lg"
                fontSize="sm"
                fontWeight="600"
                color={colors.textMuted}
                cursor="pointer"
                _hover={{ color: colors.text }}
                onClick={() => setShowRangePicker(false)}
              >
                Cancelar
              </Box>
            </Flex>
          </Box>
        )}

        {/* Confirmation modal for full verify */}
        {showConfirmModal && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(0,0,0,0.5)"
            zIndex={1000}
            display="flex"
            alignItems="center"
            justifyContent="center"
            onClick={() => setShowConfirmModal(false)}
          >
            <Box
              bg="white"
              borderRadius="xl"
              p={6}
              maxW="450px"
              mx={4}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <Flex alignItems="center" gap={3} mb={4}>
                <Box p={2} borderRadius="lg" bg="#f9eccf">
                  <LuTriangleAlert size={24} color={statusColors.warning} />
                </Box>
                <Text fontSize="lg" fontWeight="700" color={colors.text}>
                  Verificacion Completa
                </Text>
              </Flex>
              <Text fontSize="sm" color={colors.textSecondary} mb={6}>
                Esta operacion verificara todos los registros desde el inicio. Dependiendo del volumen de datos, puede
                tomar varios minutos. ¿Desea continuar?
              </Text>
              <Flex gap={3} justifyContent="flex-end">
                <Box
                  as="button"
                  px={4}
                  py={2}
                  borderRadius="lg"
                  fontSize="sm"
                  fontWeight="600"
                  color={colors.textMuted}
                  cursor="pointer"
                  _hover={{ bg: colors.bgHover }}
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancelar
                </Box>
                <Box
                  as="button"
                  px={4}
                  py={2}
                  borderRadius="lg"
                  fontSize="sm"
                  fontWeight="600"
                  bg={brand.green}
                  color="white"
                  cursor="pointer"
                  _hover={{ bg: brand.greenDark }}
                  onClick={confirmFullVerify}
                >
                  Si, verificar todo
                </Box>
              </Flex>
            </Box>
          </Box>
        )}

        {/* Loading */}
        {loading && (
          <Flex direction="column" alignItems="center" py={12} gap={3}>
            <Spinner size="lg" color={brand.green} />
            <Text fontSize="sm" color={colors.textMuted}>
              Verificando integridad...
            </Text>
          </Flex>
        )}

        {/* Results */}
        {result && !loading && (
          <>
            {/* Summary cards */}
            <Flex gap={4} mb={6}>
              <Box
                bg={colors.bgCard}
                border="1px solid"
                borderColor={colors.border}
                borderRadius="xl"
                p={4}
                flex={1}
              >
                <Text fontSize="xs" color={colors.textMuted}>
                  Verificados
                </Text>
                <Text fontSize="2xl" fontWeight="700" color={colors.text}>
                  {result.totalEntities}
                </Text>
              </Box>
              <Box bg="#eef7f6" border="1px solid" borderColor="#bfe3df" borderRadius="xl" p={4} flex={1}>
                <Text fontSize="xs" color={colors.textMuted}>
                  Validos
                </Text>
                <Text fontSize="2xl" fontWeight="700" color={statusColors.success}>
                  {result.validCount}
                </Text>
              </Box>
              <Box
                bg={result.tamperedCount > 0 ? '#fcefeb' : colors.bgCard}
                border="1px solid"
                borderColor={result.tamperedCount > 0 ? '#FECACA' : colors.border}
                borderRadius="xl"
                p={4}
                flex={1}
              >
                <Text fontSize="xs" color={colors.textMuted}>
                  Alterados
                </Text>
                <Text
                  fontSize="2xl"
                  fontWeight="700"
                  color={result.tamperedCount > 0 ? statusColors.error : colors.text}
                >
                  {result.tamperedCount}
                </Text>
              </Box>
            </Flex>

            {/* All good */}
            {result.tamperedCount === 0 && (
              <Flex direction="column" alignItems="center" py={8} gap={3}>
                <LuShieldCheck size={48} color={statusColors.success} />
                <Text fontSize="lg" fontWeight="600" color={statusColors.success}>
                  Todos los registros estan intactos
                </Text>
                <Text fontSize="sm" color={colors.textMuted}>
                  {result.validCount} registros verificados
                </Text>
              </Flex>
            )}

            {/* Tampered table */}
            {result.tamperedEntities.length > 0 && (
              <Box
                bg={colors.bgPanel}
                border="1px solid"
                borderColor={colors.border}
                borderRadius="xl"
                overflow="hidden"
              >
                <Box px={5} py={3} borderBottom="1px solid" borderColor={colors.border}>
                  <Text fontSize="sm" fontWeight="600" color={statusColors.error}>
                    Registros con alteraciones detectadas
                  </Text>
                </Box>
                <Box overflowX="auto">
                  <ChakraTable.Root size="sm">
                    <ChakraTable.Header>
                      <ChakraTable.Row>
                        <ChakraTable.ColumnHeader px={5} py={3} fontSize="xs" color={colors.textMuted}>
                          Estado
                        </ChakraTable.ColumnHeader>
                        <ChakraTable.ColumnHeader px={5} py={3} fontSize="xs" color={colors.textMuted}>
                          Entity ID
                        </ChakraTable.ColumnHeader>
                        <ChakraTable.ColumnHeader px={5} py={3} fontSize="xs" color={colors.textMuted}>
                          Version Alterada
                        </ChakraTable.ColumnHeader>
                        <ChakraTable.ColumnHeader px={5} py={3} fontSize="xs" color={colors.textMuted}>
                          Total Registros
                        </ChakraTable.ColumnHeader>
                        <ChakraTable.ColumnHeader px={5} py={3} fontSize="xs" color={colors.textMuted}>
                          Acciones
                        </ChakraTable.ColumnHeader>
                      </ChakraTable.Row>
                    </ChakraTable.Header>
                    <ChakraTable.Body>
                      {result.tamperedEntities.map((entity) => (
                        <ChakraTable.Row
                          key={entity.entityId}
                          _hover={{ bg: colors.bgHover }}
                          cursor="pointer"
                          onClick={() => navigate(`/app/auditoria/${entityName}/${entity.entityId}`)}
                        >
                          <ChakraTable.Cell px={5} py={3}>
                            <LuShieldAlert size={18} color={statusColors.error} />
                          </ChakraTable.Cell>
                          <ChakraTable.Cell px={5} py={3}>
                            <Text fontSize="xs" fontFamily="mono" color={colors.text}>
                              {entity.entityId.substring(0, 8)}...
                            </Text>
                          </ChakraTable.Cell>
                          <ChakraTable.Cell px={5} py={3}>
                            <Text fontSize="sm" color={statusColors.error} fontWeight="600">
                              v{entity.firstTamperedVersion}
                            </Text>
                          </ChakraTable.Cell>
                          <ChakraTable.Cell px={5} py={3}>
                            <Text fontSize="sm" color={colors.text}>
                              {entity.totalRecords}
                            </Text>
                          </ChakraTable.Cell>
                          <ChakraTable.Cell px={5} py={3}>
                            <Text
                              as="button"
                              fontSize="xs"
                              color={brand.green}
                              fontWeight="600"
                              cursor="pointer"
                              _hover={{ textDecoration: 'underline' }}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                navigate(`/app/auditoria/${entityName}/${entity.entityId}`)
                              }}
                            >
                              Ver Historial
                            </Text>
                          </ChakraTable.Cell>
                        </ChakraTable.Row>
                      ))}
                    </ChakraTable.Body>
                  </ChakraTable.Root>
                </Box>
              </Box>
            )}
          </>
        )}
      </MotionBox>
    </VStack>
  )
}
